"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Client state machine for the visible "Enhance prompt" action.
 *
 * Talks to POST /api/ai/improve-prompt (free — no build credits) and, on a
 * successful rewrite, REPLACES the composer text in place (via `setPrompt`) so
 * the enhanced prompt shows in the composer itself — no separate preview card.
 * The pre-enhancement text is remembered so "Revert" restores exactly what the
 * user typed. Failure / nothing-to-add is surfaced via `onNotice` (a toast) and
 * leaves the composer untouched.
 *
 * Invariants:
 *   - On a real rewrite, the composer text is swapped to the enhanced text and
 *     `active` becomes true (the composer shows a "Revert" control + can flag
 *     the build as already-improved so the silent server enhancer is skipped).
 *   - "Revert" restores the captured original and clears `active`.
 *   - Failure / no-op is non-blocking: a notice fires and the composer is left
 *     exactly as it was (never `active`).
 *   - While a run is in flight, further run() calls are ignored (the trigger
 *     is also disabled) — repeated clicks can't stack duplicate requests.
 *   - Transient failures (network drop, 5xx, model timeout/outage) get ONE
 *     automatic retry after a short delay; the final failure toast carries a
 *     "Try again" action. Auth (401) and rate-limit (429) never auto-retry.
 */

/** One automatic retry per click, only for transient failures. */
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 1200;

export interface NoticeAction {
  label: string;
  onClick: () => void;
}

export interface UseImprovePromptOptions {
  /** Current composer text. Read fresh on each run. */
  getPrompt: () => string;
  /** Write text into the composer (used to apply the rewrite and to revert). */
  setPrompt: (text: string) => void;
  /** Non-blocking feedback for failure / nothing-to-add. */
  onNotice?: (
    message: string,
    tone: "info" | "error",
    opts?: { action?: NoticeAction },
  ) => void;
}

export interface UseImprovePromptReturn {
  /** Enhancement request in flight (drives the trigger's spinner). */
  loading: boolean;
  /** True while an enhanced prompt is applied to the composer (show "Revert"). */
  active: boolean;
  /** Enhance the current composer text; on success, replace it in place. */
  run: () => void;
  /** Restore the pre-enhancement prompt. */
  revert: () => void;
}

interface ImproveResponse {
  enhanced?: unknown;
  changed?: unknown;
  failed?: unknown;
  code?: unknown;
}

/** How a single fetch attempt resolved — drives retry/messaging. */
type AttemptOutcome =
  | { kind: "applied" }
  | { kind: "unchanged" }
  | { kind: "stale" }
  | { kind: "superseded" }
  | { kind: "auth" }
  | { kind: "rate_limited" }
  | { kind: "transient"; timeout: boolean };

export function useImprovePrompt(
  opts: UseImprovePromptOptions,
): UseImprovePromptReturn {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);

  // The exact prompt captured just before applying an enhancement, so "Revert"
  // restores what the user typed even after they edit the enhanced text.
  const originalRef = useRef<string>("");
  // Duplicate-click guard: state updates are async, so the disabled trigger
  // alone can't stop a same-tick double invocation — this ref can.
  const loadingRef = useRef(false);
  // Abort the in-flight request on revert/unmount, and ignore stale results.
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  // Keep callbacks fresh without re-creating run/revert every render (updated
  // in an effect so we never write a ref during render).
  const optsRef = useRef(opts);
  // Lets the "Try again" toast action re-invoke run() without a render dep.
  const runRef = useRef<() => void>(() => {});
  useEffect(() => {
    optsRef.current = opts;
  });

  const run = useCallback(() => {
    if (loadingRef.current) return; // one enhancement at a time
    const prompt = optsRef.current.getPrompt();
    if (prompt.trim().length < 8) return; // trigger also guards

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const runId = ++runIdRef.current;

    loadingRef.current = true;
    setLoading(true);

    const finish = () => {
      loadingRef.current = false;
      setLoading(false);
    };

    const notifyFailure = (message: string) => {
      optsRef.current.onNotice?.(message, "error", {
        action: { label: "Try again", onClick: () => runRef.current() },
      });
    };

    /** One fetch attempt. Applies the rewrite itself on success. */
    const attempt = async (): Promise<AttemptOutcome> => {
      let res: Response;
      try {
        res = await fetch("/api/ai/improve-prompt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt }),
          signal: controller.signal,
        });
      } catch {
        // Network drop or abort. Abort/supersede = silent bail.
        if (controller.signal.aborted || runId !== runIdRef.current) {
          return { kind: "superseded" };
        }
        return { kind: "transient", timeout: false };
      }
      if (runId !== runIdRef.current) return { kind: "superseded" };

      if (res.status === 401) return { kind: "auth" };
      if (res.status === 429) return { kind: "rate_limited" };
      if (!res.ok) return { kind: "transient", timeout: false };

      let data: ImproveResponse;
      try {
        data = (await res.json()) as ImproveResponse;
      } catch {
        return { kind: "transient", timeout: false };
      }
      if (runId !== runIdRef.current) return { kind: "superseded" };

      if (data.failed === true) {
        return { kind: "transient", timeout: data.code === "timeout" };
      }

      // If the user edited the composer while the request was in flight, the
      // rewrite is for stale text — never overwrite their live edit. Keep what
      // they typed; they can re-run Enhance on the new text.
      if (optsRef.current.getPrompt() !== prompt) return { kind: "stale" };

      const enhanced = typeof data.enhanced === "string" ? data.enhanced : null;
      const changed = data.changed === true && enhanced !== null;
      if (!changed) return { kind: "unchanged" };

      // Apply in place: remember the original, swap in the enhanced text.
      originalRef.current = prompt;
      optsRef.current.setPrompt(enhanced);
      setActive(true);
      return { kind: "applied" };
    };

    void (async () => {
      for (let attemptNo = 1; attemptNo <= MAX_ATTEMPTS; attemptNo++) {
        const outcome = await attempt();

        switch (outcome.kind) {
          case "superseded":
            // A newer run/revert owns the state (including `loading`) now.
            return;
          case "applied":
            finish();
            return;
          case "unchanged":
            finish();
            optsRef.current.onNotice?.(
              "Your prompt already looks clear — nothing to add.",
              "info",
            );
            return;
          case "stale":
            finish();
            optsRef.current.onNotice?.(
              "Your prompt changed while enhancing — press Enhance again to update it.",
              "info",
            );
            return;
          case "auth":
            finish();
            optsRef.current.onNotice?.(
              "Please sign in to enhance your prompt.",
              "error",
            );
            return;
          case "rate_limited":
            finish();
            notifyFailure(
              "You're enhancing too quickly — give it a few seconds, then try again.",
            );
            return;
          case "transient": {
            const retryable =
              attemptNo < MAX_ATTEMPTS &&
              // Retrying a rewrite of stale text would be wasted work — the
              // result would be discarded by the stale-input check anyway.
              optsRef.current.getPrompt() === prompt;
            if (retryable) {
              await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
              if (controller.signal.aborted || runId !== runIdRef.current) {
                return;
              }
              continue;
            }
            finish();
            notifyFailure(
              outcome.timeout
                ? "Enhancing took too long — your prompt is unchanged."
                : "Couldn't enhance right now — your prompt is unchanged.",
            );
            return;
          }
        }
      }
    })();
  }, []);
  useEffect(() => {
    runRef.current = run;
  });

  const revert = useCallback(() => {
    abortRef.current?.abort();
    runIdRef.current++; // invalidate any in-flight run
    optsRef.current.setPrompt(originalRef.current);
    setActive(false);
    loadingRef.current = false;
    setLoading(false);
  }, []);

  return { loading, active, run, revert };
}
